import React, { useEffect, useRef } from "react";
import { HiArrowRight, HiCheckCircle } from "react-icons/hi";

interface AboutSectionProps {
  lang?: "en" | "it";
}

// --- Copy ---
const copy = {
  en: {
    heading: "Who We Are",
    tagline:
      "Connecting Bangladeshi and Italian businesses through trade, investment, innovation, and strategic partnerships.",
    paragraphs: [
      "The Bangladesh Italy Chamber of Commerce and Industry (BICCI) is a bilateral trade platform dedicated to strengthening economic cooperation between Bangladesh and Italy.",
      "The Chamber supports companies, entrepreneurs, institutions, and investors seeking to establish, expand, or deepen commercial relations between the two countries.",
      "BICCI brings together corporations, SMEs, entrepreneurs, and industry stakeholders, providing access to strategic networks, market insights, institutional dialogue, and business support services necessary to operate successfully in today's global marketplace.",
    ],
    highlights: [
      "Bilateral business platform between Bangladesh and Italy",
      "Trade missions, networking events and institutional engagement",
      "Support for trade, investment and market access",
      "Focus on long-term business partnerships and economic cooperation",
    ],
  },
  it: {
    heading: "Chi Siamo",
    tagline:
      "Collegare le imprese del Bangladesh e dell'Italia attraverso commercio, investimenti, innovazione e partnership strategiche.",
    paragraphs: [
      "La Camera di Commercio Italia-Bangladesh (BICCI) è la piattaforma bilaterale dedicata a rafforzare la cooperazione economica e commerciale tra Italia e Bangladesh.",
      "La Camera supporta aziende, imprenditori, istituzioni e investitori italiani e bangladesi che desiderano avviare, espandere o consolidare relazioni commerciali tra i due paesi, facilitando chi vuole fare impresa o investire in Bangladesh.",
      "BICCI riunisce grandi aziende, PMI, imprenditori e operatori del settore, offrendo accesso a reti strategiche, analisi di mercato, dialogo istituzionale e servizi di supporto alle imprese italiane interessate a importare dal Bangladesh o aprire una società nel paese.",
    ],
    highlights: [
      "Piattaforma bilaterale di commercio tra Bangladesh e Italia",
      "Missioni commerciali, eventi di networking e impegno istituzionale",
      "Supporto per il commercio, gli investimenti e l\u2019accesso al mercato bangladese",
      "Attenzione a partnership commerciali e cooperazione economica di lungo termine",
    ],
  },
};

// --- Shared Reveal Logic ---
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll<HTMLElement>("[data-r]").forEach((node, i) => {
            setTimeout(() => {
              node.style.opacity = "1";
              node.style.transform = "translateY(0) translateX(0)";
            }, i * 80); // Slightly accelerated step for snappier execution
          });
          obs.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

const hide = (dir: "up" | "left" | "right" = "up"): React.CSSProperties => ({
  opacity: 0,
  transform:
    dir === "up"
      ? "translateY(30px)"
      : dir === "left"
        ? "translateX(-40px)"
        : "translateX(40px)",
  transition:
    "opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1), transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
});

// --- Component 1: About Us ---
const AboutUsRow = ({ lang = "en" }: AboutSectionProps) => {
  const t = copy[lang];
  const ref = useReveal();
  return (
    <div
      ref={ref}
      className="w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28"
    >
      {/* Centered Heading Title Block */}
      <div className="max-w-3xl mx-auto text-center mb-16 md:mb-24">
        <h2
          data-r
          style={hide("up")}
          className="text-4xl md:text-5xl font-extrabold text-[#0F1C35] tracking-tight"
        >
          {t.heading}
        </h2>
      </div>

      {/* Split Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* LEFT COLUMN: ALL-IMAGE MOSAIC */}
        <div
          data-r
          style={hide("left")}
          className="lg:col-span-6 flex gap-4 h-[480px] w-full"
        >
          <div className="w-[45%] flex flex-col gap-4">
            {/* Top Left Image */}
            <div
              className="overflow-hidden shadow-sm border border-gray-100 bg-gray-50 rounded-2xl"
              style={{ flex: "0 0 54%" }}
            >
              <img
                src="https://www.webuildvalue.com/wp-content/uploads/2024/12/Porti_italiani_02.jpg"
                alt="Chamber Connection"
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
              />
            </div>
            {/* Bottom Left Image */}
            <div className="flex-1 overflow-hidden shadow-sm border border-gray-100 bg-gray-50 rounded-2xl">
              <img
                src="https://apparelresources.com/wp-content/uploads/2025/01/Over-50000-garment-workers-left-jobless-as-Bangladeshs-apparel-sector-faces-crisis.jpg"
                alt="Corporate Innovation"
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
              />
            </div>
          </div>

          {/* Right Image (Tall) */}
          <div className="flex-1 overflow-hidden shadow-md border border-gray-100 bg-gray-50 rounded-2xl">
            <img
              src="https://www.rossi.com/-/media/Feature/Rossi-Group/Global-presence/ACs-pages/Rossi-ACs-single-pages/Italy/Modena-factory-assembly.png"
              alt="Strategic Headquarters"
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.02]"
            />
          </div>
        </div>

        {/* RIGHT COLUMN: TEXT BLOCK */}
        <div className="lg:col-span-6 flex flex-col items-start space-y-5 text-[#3D4A62] text-base md:text-md leading-relaxed font-normal">
          {/* Main Tagline */}
          <h3
            data-r
            className="text-2xl md:text-3xl font-extrabold text-[#0F1C35] leading-snug tracking-tight border-l-4 border-[#0077b6] pl-5 py-1"
            style={hide("up")}
          >
            {t.tagline}
          </h3>

          {/* Body Paragraphs */}
          {t.paragraphs.map((p, i) => (
            <p
              key={i}
              data-r
              className={`text-gray-600 ${i === t.paragraphs.length - 1 ? "pb-4" : ""}`}
              style={hide("up")}
            >
              {p}
            </p>
          ))}

          {/* Highlight Checkmarks */}
          <ul data-r className="flex flex-col gap-3 w-full" style={hide("up")}>
            {t.highlights.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 text-sm md:text-base text-gray-700 font-medium"
              >
                <HiCheckCircle className="mt-1 shrink-0 text-[#0077b6] text-xl" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          {/* Learn More Button */}
          <div data-r style={hide("up")}>
            <a
              href="/about"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white text-sm font-semibold tracking-wide transition-all duration-300 hover:brightness-110 hover:gap-3"
              style={{ backgroundColor: "#0077b6" }}
            >
              Learn More
              <HiArrowRight className="text-base" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main Section Assembly ---
export default function AboutSection({ lang = "en" }: AboutSectionProps) {
  return (
    <section id="who-we-are" className="bg-white overflow-hidden font-sans">
      <AboutUsRow lang={lang} />
    </section>
  );
}
